import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { Form, message, Modal } from 'antd';
import { TooltipWithIcon } from 'ui';
import { UploadFile } from 'antd';
import { StyledListingDrawer, StyledFooter, StyledDrawerTitle } from './style';
import { Label } from 'ui/Label';
import { Tag } from '../../Tag';
import { Tabs } from '../../Tabs';
import { NewListingForm } from '../../Form/Listings/NewListingForm';
import { NewListingProductsForm } from '../../Form/Listings/NewListingProductsForm';
import { NewListingDeliveryConditionsForm } from '../../Form/Listings/NewListingDeliveryConditionsForm';
import { NewListingContractTermsForm } from '../../Form/Listings/NewListingContractTermsForm';
import { ListingSummary } from '../../Form/Listings/ListingSummary';
import { Button } from '../../Button';
import { Spinner } from '../../Spinner';
import { Icon } from 'ui/Icon';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useAuth } from 'hooks/useAuth';
import { useAutoSave } from '../../../hooks/useAutoSave';
import dayjs from 'dayjs';

import {
  Listing,
  ListingProduct,
  DeliveryConditionsData,
  DeliveryProductReference,
  ContractTerms,
  DraftSaveStatus,
  ListingContractDocument,
} from '../../../types/ListingData';
import {
  GROUPED_QUALITY_MARKS_URL,
  QualityMarkOption,
  toListingQualityMarkOptions,
} from 'utils/qualityMarkHelpers';

declare global {
  interface Window {
    saveDraftResolve?:
      | ((appId?: string | number | null) => void)
      | (() => void);
    saveDraftReject?: () => void;
  }
}

interface CustomUploadFile extends UploadFile {
  file_path?: string;
  response?: {
    file_path?: string;
  };
  fileId?: number;
  isExisting?: boolean;
}

interface DrawerCreateListingProps {
  farmId?: number | null;
  listingId?: string | number | null;
  open?: boolean;
  onClose?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  closeDrawer?: (value: boolean) => void;
  setReload: React.Dispatch<React.SetStateAction<number>>;
  onListingCreated?: (listingId: string) => void;
  viewOnly?: boolean;
  startOnFirstTab?: boolean;
}

const EDITABLE_STATUSES = [
  'unpublished',
  'published',
  'has_offers',
  'unsuccessful',
  'cancelled',
];

const PUBLISHABLE_STATUSES = ['unpublished', 'unsuccessful', 'cancelled'];

export const DrawerCreateUpdateListing = ({
  open,
  onClose,
  closeDrawer,
  farmId,
  listingId,
  setReload,
  onListingCreated,
  viewOnly = false,
  startOnFirstTab = false,
}: DrawerCreateListingProps) => {
  const { data: ignoreDeadlineSetting } = useQueryApiClient({
    request: { url: '/api/v2/settings/value/ignore_offer_deadline_validation' },
  });
  const ignoreDeadlineValidation = (() => {
    if (ignoreDeadlineSetting == null) return false;
    const stripped = String(ignoreDeadlineSetting)
      .replace(/<[^>]*>/g, '')
      .trim();
    return stripped === '1' || stripped === 'true';
  })();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [lastSavedData, setLastSavedData] = useState<Partial<Listing> | null>(
    null
  );
  const [isFirstSave, setIsFirstSave] = useState<boolean>(true);
  const [isProductsFormOpen, setIsProductsFormOpen] = useState<boolean>(false);
  const [currentProducts, setCurrentProducts] = useState<ListingProduct[]>([]);
  const [currentDeliveryData, setCurrentDeliveryData] = useState<
    DeliveryConditionsData[] | null
  >(null);
  const [cachedListing, setCachedListing] = useState<Listing | null>(null);

  const [currentContractDocuments, setCurrentContractDocuments] = useState<
    ListingContractDocument[]
  >([]);
  const [tabValidationErrors, setTabValidationErrors] = useState<{
    [tabKey: string]: number;
  }>({});
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [isDataChecked, setIsDataChecked] = useState<boolean>(false);
  const [isCheckingData, setIsCheckingData] = useState<boolean>(false);

  const compareFieldsArray = [
    'title',
    'transaction_type',
    'transaction_context',
    'description',
    'is_private',
    'single_producer_only',
    'private_link_full',
    'publication_start_date',
    'publication_end_date',
    'auto_extend_publication',
    'extend_days',
    'delivery_radius_km',
    'address',
    'contract_term_start_date',
    'contract_term_end_date',
    'payment_conditions',
    'additional_contract_terms',
    'contract_document',
  ];

  const [form] = Form.useForm();
  const setDraftSaveStatusRef = useRef<
    ((status: DraftSaveStatus) => void) | null
  >(null);

  const mergeProductsWithBackendIds = (
    existingProducts: ListingProduct[],
    backendProducts: ListingProduct[]
  ): ListingProduct[] => {
    if (!backendProducts || backendProducts.length === 0) {
      return existingProducts;
    }

    const mergedProducts: ListingProduct[] = [];

    existingProducts.forEach((existingProduct) => {
      const backendProduct = backendProducts.find(
        (p) => p.category_id === existingProduct.category_id
      );
      if (backendProduct?.id) {
        mergedProducts.push({
          ...existingProduct,
          id: backendProduct.id,
        });
      } else {
        mergedProducts.push(existingProduct);
      }
    });

    backendProducts.forEach((backendProduct) => {
      const alreadyExists = mergedProducts.some(
        (p) => p.category_id === backendProduct.category_id
      );
      if (!alreadyExists) {
        mergedProducts.push(backendProduct);
      }
    });

    return mergedProducts;
  };

  const serializeContractTerms = useCallback(
    (
      contract_start_date?: dayjs.Dayjs,
      contract_end_date?: dayjs.Dayjs,
      payment_conditions?: string,
      additional_contract_terms?: string
    ): Partial<ContractTerms> => {
      const contractTermsData: Partial<ContractTerms> = {};

      if (contract_start_date) {
        contractTermsData.contract_start_date =
          contract_start_date.format('YYYY-MM-DD');
      }
      if (contract_end_date) {
        contractTermsData.contract_end_date =
          contract_end_date.format('YYYY-MM-DD');
      }
      if (payment_conditions) {
        contractTermsData.payment_conditions = payment_conditions;
      }
      if (additional_contract_terms) {
        contractTermsData.additional_contract_terms = additional_contract_terms;
      }

      const hasAnyContractTerms = Object.values(contractTermsData).some(
        (value) => value !== undefined && value !== ''
      );

      return hasAnyContractTerms ? contractTermsData : {};
    },
    []
  );

  const processListingDataForForm = useCallback(
    (data: Listing): Record<string, unknown> => {
      const processedData: Record<string, unknown> = { ...data };

      if (data.publication_start_date) {
        processedData.publication_start_date = dayjs(
          data.publication_start_date
        );
      }
      if (data.publication_end_date) {
        processedData.publication_end_date = dayjs(data.publication_end_date);
      }

      if (data.contract_terms) {
        processedData.contract_term_start_date = data.contract_terms
          .contract_start_date
          ? dayjs(data.contract_terms.contract_start_date)
          : undefined;
        processedData.contract_term_end_date = data.contract_terms
          .contract_end_date
          ? dayjs(data.contract_terms.contract_end_date)
          : undefined;
        processedData.payment_conditions =
          data.contract_terms.payment_conditions;
        processedData.additional_contract_terms =
          data.contract_terms.additional_contract_terms;
      }

      return processedData;
    },
    []
  );

  const handleDeliveryDataChange = (data: DeliveryConditionsData[]) => {
    setCurrentDeliveryData(data);
    if (tabValidationErrors.add_delivery_conditions) {
      setTabValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.add_delivery_conditions;
        return newErrors;
      });
    }
    if (isDataChecked) {
      setIsDataChecked(false);
    }
  };

  const handleContractDocumentsChange = (
    documents: ListingContractDocument[]
  ) => {
    setCurrentContractDocuments(documents);
  };

  const handleContractDocumentDelete = (document: ListingContractDocument) => {
    setCurrentContractDocuments((prev) =>
      prev.filter((d) => {
        if (d.id && document.id && d.id === document.id) return false;
        if (d.file_id && document.file_id && d.file_id === document.file_id)
          return false;
        if (
          d.file_name &&
          document.file_name &&
          d.file_name === document.file_name &&
          !d.id &&
          !document.id &&
          !d.file_id &&
          !document.file_id
        ) {
          return false;
        }
        return true;
      })
    );
  };

  const handleFileDownload = (file: CustomUploadFile) => {
    const currentListingId = draftId || listingId;
    if (currentListingId && file.fileId) {
      const downloadUrl = `${window.runConfig?.backendUrl}/api/v2/listings/${currentListingId}/contract-terms/download?file_id=${file.fileId}`;
      window.open(downloadUrl, '_blank');
      return;
    }

    if (file.originFileObj) {
      const url = URL.createObjectURL(file.originFileObj);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const intl = useIntl();
  const { user } = useAuth();

  const isEditableStatus = (status: string | undefined) => {
    return status && EDITABLE_STATUSES.includes(status);
  };

  const isPublishableStatus = (status: string | undefined) => {
    return status && PUBLISHABLE_STATUSES.includes(status);
  };

  const stepFieldMap: Record<string, string[]> = {
    add_listing_info: [
      'title',
      'identifier',
      'transaction_type',
      'transaction_context',
      'description',
      'address',
      'is_private',
      'private_link',
      'single_producer_only',
      'product_only_application',
      'is_recycled',
      'publication_start_date',
      'publication_end_date',
      'extend_days',
      'delivery_radius_km',
    ],
    add_listing_products: ['products_items'],
    add_delivery_conditions: [
      'delivery_type',
      'contract_period_type',
      'contract_start_date',
      'contract_end_date',
      'delivery_period_value',
      'delivery_period_unit',
      'delivery_frequency_value',
      'delivery_frequency_unit',
      'free_form_description',
    ],
    add_listing_contract_terms: [
      'contract_term_start_date',
      'contract_term_end_date',
      'payment_conditions',
      'additional_contract_terms',
    ],
  };

  const { appendData: publishListing, isLoading: isPublishingApi } =
    useQueryApiClient({
      request: {
        url: `api/v2/listings/:listingId/publish`,
        method: 'POST',
      },
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'listings.published' }));
        handleSuccess();
      },
    });

  const { appendData: saveDraftData, isLoading: isDraftSaving } =
    useQueryApiClient({
      request: {
        url:
          isFirstSave || !draftId
            ? 'api/v2/listings'
            : `api/v2/listings/${draftId}?_method=PATCH`,
        method: 'POST',
      },
      onSuccess: (response, passOnSuccess) => {
        const listingIdFromResponse = response?.id || draftId;

        const previousDocuments =
          (lastSavedData as any)?.contract_documents || [];

        if (isFirstSave && response?.id) {
          setDraftId(response.id);
          setIsFirstSave(false);
          onListingCreated?.(response.id);
        }

        if (response) {
          const { products, delivery_conditions, ...dataWithoutProducts } =
            response;
          const processedData = processListingDataForForm({
            ...dataWithoutProducts,
          } as Listing);

          form.setFieldsValue(processedData);

          if (response.products) {
            const mergedProducts = mergeProductsWithBackendIds(
              currentProducts,
              response.products
            );
            setCurrentProducts(mergedProducts);
            form.setFieldValue('products_items', mergedProducts);
          }

          if (response.delivery_conditions) {
            setCurrentDeliveryData(response.delivery_conditions);
          }

          setCachedListing(response);

          const completeLastSavedData = { ...processedData };
          compareFieldsArray.forEach((field) => {
            if (!(field in completeLastSavedData)) {
              completeLastSavedData[field] = undefined;
            }
          });

          setLastSavedData({
            ...completeLastSavedData,
            products_items: response.products || [],
            delivery_conditions: response.delivery_conditions || [],
            contract_documents: currentContractDocuments,
          } as Partial<Listing>);

          setTimeout(() => {
            const currentFormValues = form.getFieldsValue(true);
            const updatedLastSaved: Record<string, unknown> = {
              ...currentFormValues,
              products_items: currentProducts,
              delivery_conditions: currentDeliveryData,
              contract_documents: currentContractDocuments,
            };

            compareFieldsArray.forEach((field) => {
              if (!(field in updatedLastSaved)) {
                updatedLastSaved[field] = undefined;
              }
            });

            setLastSavedData(updatedLastSaved as Partial<Listing>);
          }, 0);
        }

        if (setDraftSaveStatusRef.current) {
          setDraftSaveStatusRef.current({
            status: 'saved',
            lastSaved: new Date(),
          });
        }
        message.destroy();
        const saveContext = passOnSuccess?.context as string | undefined;
        if (saveContext !== 'publishing') {
          const isPublishedOrHasOffers =
            cachedListing?.status === 'published' ||
            cachedListing?.status === 'has_offers';

          const messageKey =
            saveContext === 'data_check'
              ? 'general.data_checked'
              : isPublishedOrHasOffers
                ? 'general.data_updated'
                : 'general.draft_saved';
          message.success(intl.formatMessage({ id: messageKey }));
        }

        const listingIdToUse = listingIdFromResponse;

        if (
          listingIdToUse &&
          currentContractDocuments &&
          currentContractDocuments.length > 0
        ) {
          const pendingDocs = currentContractDocuments.filter(
            (d) => d.isPending && d.originFile
          );

          if (pendingDocs.length > 0) {
            const fd = new FormData();
            pendingDocs.forEach((d) => {
              if (d.originFile) {
                fd.append('files[]', d.originFile);
              }
            });
            uploadContractFiles(
              fd,
              { listingId: listingIdToUse },
              { listingId: listingIdToUse, previousDocuments }
            );
          } else {
            runDeferredDeletes(listingIdToUse, previousDocuments);
          }
        } else {
          if (listingIdToUse) {
            runDeferredDeletes(listingIdToUse, previousDocuments);
          } else {
            setReload((old) => old + 1);

            if (window.saveDraftResolve) {
              window.saveDraftResolve(listingIdFromResponse || undefined);
              window.saveDraftResolve = undefined;
              window.saveDraftReject = undefined;
            }
          }
        }
      },
      onError: (error) => {
        if (setDraftSaveStatusRef.current) {
          setDraftSaveStatusRef.current({
            status: 'error',
            error: error.message,
          });
        }
        message.error(intl.formatMessage({ id: 'general.draft_save_failed' }));
        message.destroy();

        if (window.saveDraftReject) {
          window.saveDraftReject();
          window.saveDraftResolve = undefined;
          window.saveDraftReject = undefined;
        }
      },
    });

  const { data: listingData, isLoading: isLoadingListing } = useQueryApiClient({
    request: {
      url: `/api/v2/listings/${listingId}`,
      method: 'GET',
    },
    enabled: !!listingId && open,
    onSuccess: (data) => {
      if (data && form) {
        const { products, delivery_conditions, ...dataWithoutProducts } = data;
        const processedData = processListingDataForForm({
          ...dataWithoutProducts,
        } as Listing);

        form.setFieldsValue(processedData);

        if (data.products) {
          const mergedProducts = mergeProductsWithBackendIds(
            currentProducts,
            data.products
          );
          setCurrentProducts(mergedProducts);
          form.setFieldValue('products_items', mergedProducts);
        }

        if (data.delivery_conditions) {
          setCurrentDeliveryData(data.delivery_conditions);
        }

        let serverDocuments: ListingContractDocument[] = [];
        if (
          data.contract_terms?.files &&
          Array.isArray(data.contract_terms.files) &&
          data.contract_terms.files.length > 0
        ) {
          serverDocuments = data.contract_terms.files.map((file: any) => ({
            file_id: file.id || file.file_id,
            file_path: file.url || file.file_path,
            file_name: file.name || file.original_name || file.file_name,
            file_size: file.size_bytes || file.file_size,
            mime_type: file.mime_type,
            created_at: file.created_at,
            file: file,
            isPending: false,
          }));
        }
        setCurrentContractDocuments(serverDocuments);

        setCachedListing(data);

        setDraftId(data.id);
        setIsFirstSave(false);

        const completeLastSavedData: Record<string, unknown> = {
          ...processedData,
          products_items: data.products,
          delivery_conditions: data.delivery_conditions,
          contract_documents: serverDocuments,
        };
        compareFieldsArray.forEach((field) => {
          if (!(field in completeLastSavedData)) {
            completeLastSavedData[field] = undefined;
          }
        });

        setLastSavedData(completeLastSavedData as Partial<Listing>);
        if (setDraftSaveStatusRef.current) {
          setDraftSaveStatusRef.current({ status: 'saved' });
        }

        if (
          !startOnFirstTab &&
          (!isEditableStatus(data.status) || data.status === 'published')
        ) {
          setCurrentStep(4);
        }
      }
    },
  });

  const { data: allQualityMarks } = useQueryApiClient({
    request: {
      url: GROUPED_QUALITY_MARKS_URL,
    },
    enabled: open,
  });

  const convertBlobToJson = async (blob: Blob) => {
    try {
      const text = await blob.text();
      return JSON.parse(text);
    } catch (error) {
      throw new Error('Invalid response format');
    }
  };

  const { appendData: uploadContractFiles, isLoading: isUploadingFile } =
    useQueryApiClient({
      request: {
        url: '/api/v2/listings/:listingId/contract-terms/upload',
        method: 'POST',
        multipart: true,
      },
      onSuccess: async (resp, ctx) => {
        const listingIdFromUpload = ctx?.listingId;
        const previousDocuments = ctx?.previousDocuments as
          | ListingContractDocument[]
          | undefined;

        let responseData = resp;
        if (resp instanceof Blob) {
          try {
            const text = await resp.text();
            responseData = JSON.parse(text);
          } catch (e) {
            runDeferredDeletes(listingIdFromUpload, previousDocuments);
            return;
          }
        }

        const uploadedFiles = Array.isArray(responseData?.files)
          ? responseData.files
          : [];

        setCurrentContractDocuments((prev) => {
          let uploadedIndex = 0;

          const updated = prev.map((doc) => {
            if (doc.isPending) {
              const uploadedFile = uploadedFiles[uploadedIndex];
              uploadedIndex++;

              return {
                ...doc,
                isPending: false,
                ...(uploadedFile && {
                  file_id:
                    uploadedFile.file_id || uploadedFile.id || doc.file_id,
                  file_path: uploadedFile.file_path || doc.file_path,
                  file_name:
                    uploadedFile.original_name ||
                    uploadedFile.file_name ||
                    doc.file_name,
                  created_at: uploadedFile.created_at || doc.created_at,
                }),
              };
            }
            return doc;
          });

          setLastSavedData((lastSaved) => {
            if (lastSaved) {
              return {
                ...lastSaved,
                contract_documents: updated,
              };
            }
            return lastSaved;
          });

          return updated;
        });

        if (listingIdFromUpload) {
          runDeferredDeletes(listingIdFromUpload, previousDocuments);
        } else {
          setReload((old) => old + 1);
        }
      },
      onError: async (errorBlob) => {
        try {
          const errorData = await convertBlobToJson(errorBlob);
          message.error(
            errorData.error ||
              intl.formatMessage({ id: 'listings.form.file_upload_error' })
          );
        } catch (parseError) {
          message.error(
            intl.formatMessage({ id: 'listings.form.file_upload_error' })
          );
        }
        if (setDraftSaveStatusRef.current) {
          setDraftSaveStatusRef.current({ status: 'idle' });
        }
        message.destroy();

        if (window.saveDraftReject) {
          window.saveDraftReject();
          window.saveDraftResolve = undefined;
          window.saveDraftReject = undefined;
        }
      },
    });

  const { appendData: deleteContractFilesBulk } = useQueryApiClient({
    request: {
      url: '/api/v2/listings/:listingId/contract-terms/file',
      method: 'DELETE',
    },
  });

  const runDeferredDeletes = (
    listingIdToUse: string | number,
    previousDocuments?: ListingContractDocument[]
  ) => {
    const before = previousDocuments || [];
    const after = currentContractDocuments || [];

    const beforeWithFileId = before.filter((d) => d && d.file_id);
    const afterWithFileId = after.filter((d) => d && d.file_id);
    const afterFileIdSet = new Set(afterWithFileId.map((d) => d.file_id));
    const removedFileIds = beforeWithFileId
      .map((d) => d.file_id as number)
      .filter((id: number) => !afterFileIdSet.has(id));

    if (removedFileIds.length === 0) {
      setReload((old) => old + 1);

      if (window.saveDraftResolve) {
        window.saveDraftResolve(listingIdToUse || undefined);
        window.saveDraftResolve = undefined;
        window.saveDraftReject = undefined;
      }
      return;
    }

    deleteContractFilesBulk(
      { file_ids: removedFileIds },
      { listingId: listingIdToUse }
    );
    setReload((old) => old + 1);

    if (window.saveDraftResolve) {
      window.saveDraftResolve(listingIdToUse || undefined);
      window.saveDraftResolve = undefined;
      window.saveDraftReject = undefined;
    }
  };

  const { data: productFormAttributes } = useQueryApiClient({
    request: {
      url: 'api/v2/products/form/attributes',
    },
    enabled: open,
  });

  const farmQualityMarkIds = useMemo(() => {
    return allQualityMarks?.map((qmark: QualityMarkOption) => qmark.id) || [];
  }, [allQualityMarks?.length, allQualityMarks?.[0]?.id]);

  const farmQualityMarksArray = useMemo(() => {
    return toListingQualityMarkOptions(allQualityMarks);
  }, [allQualityMarks?.length, allQualityMarks?.[0]?.id]);

  const productQualityMarks = useMemo(() => {
    if (!allQualityMarks || !currentProducts) return [];

    const qualityMarkIdsSet = new Set<string>();

    currentProducts.forEach((product) => {
      if (product.quality_mark_ids && Array.isArray(product.quality_mark_ids)) {
        product.quality_mark_ids.forEach((id) => {
          qualityMarkIdsSet.add(String(id));
        });
      }
    });

    return allQualityMarks.filter((qmark: QualityMarkOption) =>
      qualityMarkIdsSet.has(String(qmark.id))
    );
  }, [allQualityMarks, currentProducts]);

  const storageUnits = useMemo(
    () => productFormAttributes?.storageUnits || [],
    [productFormAttributes]
  );

  useEffect(() => {
    if (
      open &&
      form &&
      !isLoadingListing &&
      !lastSavedData &&
      allQualityMarks
    ) {
      const timeoutId = setTimeout(() => {
        const currentFormData = form.getFieldsValue(true);
        const additionalData = {
          delivery_conditions: currentDeliveryData,
        };
        const fullCurrentData = {
          ...currentFormData,
          ...additionalData,
        };

        const completeLastSavedData = { ...fullCurrentData };
        compareFieldsArray.forEach((field) => {
          if (!(field in completeLastSavedData)) {
            completeLastSavedData[field] = undefined;
          }
        });

        setLastSavedData(completeLastSavedData as Partial<Listing>);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    open,
    form,
    isLoadingListing,
    currentDeliveryData,
    lastSavedData,
    allQualityMarks,
  ]);

  const handleSuccess = () => {
    resetEverything();
    if (closeDrawer) {
      closeDrawer(false);
    }
    setReload((old) => old + 1);
  };

  const steps = useMemo(() => {
    const baseSteps = [
      {
        key: 'add_listing_info',
        label: intl.formatMessage({ id: 'navigation.base_information' }),
        content:
          listingId && isLoadingListing ? (
            <Spinner spinning={true} dontRender>
              <div>Loading...</div>
            </Spinner>
          ) : (
            <NewListingForm
              listingData={listingData}
              listingId={draftId || (listingId ? String(listingId) : null)}
              isEditing={!!listingId}
              ignoreDeadlineValidation={!!ignoreDeadlineValidation}
            />
          ),
      },
      {
        key: 'add_listing_products',
        label: intl.formatMessage({ id: 'listings.form.products' }),
        content: (
          <NewListingProductsForm
            key={listingId || 'new'}
            onFormStateChange={setIsProductsFormOpen}
            onProductsChange={(products: ListingProduct[]) => {
              setCurrentProducts(products);
              form.setFieldValue('products_items', products);
              if (tabValidationErrors.add_listing_products) {
                setTabValidationErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.add_listing_products;
                  return newErrors;
                });
              }
              handleFormValuesChange(
                { products_items: products },
                form.getFieldsValue()
              );
            }}
            existingProducts={currentProducts}
            farmQualityMarks={farmQualityMarksArray}
            farmQualityMarkIds={farmQualityMarkIds}
            storageUnits={storageUnits}
            hasValidationError={!!tabValidationErrors.add_listing_products}
          />
        ),
      },
      {
        key: 'add_delivery_conditions',
        label: intl.formatMessage({ id: 'listings.form.delivery_conditions' }),
        content: (
          <NewListingDeliveryConditionsForm
            key={`delivery-form-${listingId || 'new'}`}
            deliveryConditionsData={currentDeliveryData || undefined}
            isEditing={!!listingId}
            availableProducts={currentProducts}
            onDeliveryDataChange={handleDeliveryDataChange}
          />
        ),
      },
      {
        key: 'add_listing_contract_terms',
        label: intl.formatMessage({ id: 'listings.form.contract_terms' }),
        content: (
          <NewListingContractTermsForm
            initialDocuments={currentContractDocuments}
            onDocumentsChange={handleContractDocumentsChange}
            listingId={draftId || (listingId ? String(listingId) : null)}
            onDownloadFile={handleFileDownload}
            onDeleteFile={(file) => {
              const doc = currentContractDocuments.find(
                (d) =>
                  d.file_id === (file as CustomUploadFile).fileId ||
                  d.file_name === file.name
              );
              if (doc) {
                handleContractDocumentDelete(doc);
              }
            }}
          />
        ),
      },
    ];

    const canShowSummary =
      isDataChecked ||
      (!!cachedListing &&
        (!isEditableStatus(cachedListing.status) ||
          cachedListing.status === 'published'));

    if (canShowSummary) {
      baseSteps.push({
        key: 'add_listing_summary',
        label: intl.formatMessage({ id: 'listings.form.summary' }),
        content: (
          <ListingSummary
            formData={form.getFieldsValue(true)}
            products={currentProducts}
            deliveryConditions={currentDeliveryData}
            contractFiles={currentContractDocuments.map((doc) => ({
              uid: String(doc.file_id || doc.id || Math.random()),
              name: doc.file_name || 'contract.pdf',
              status: 'done' as const,
              url: doc.file_path,
              file_path: doc.file_path,
              file_name: doc.file_name,
              file_size: doc.file_size,
              file_format: doc.mime_type,
              created_at: doc.created_at,
              fileId: doc.file_id,
            }))}
            qualityMarks={
              allQualityMarks?.map((qmark: QualityMarkOption) => ({
                id: qmark.id,
                name: qmark.name,
                logo: qmark.logo,
              })) || []
            }
            onEditProduct={(id) => {
              setCurrentStep(1);
              setIsDataChecked(false);
            }}
            onDeleteProduct={(id) => {
              const updated = currentProducts.filter((p) => p.id !== id);
              setCurrentProducts(updated);
              form.setFieldValue('products_items', updated);

              if (currentDeliveryData) {
                const updatedDelivery = currentDeliveryData.map((delivery) => {
                  if (delivery.products && Array.isArray(delivery.products)) {
                    const products = delivery.products;
                    const filteredProducts = (
                      products as Array<
                        ListingProduct | DeliveryProductReference
                      >
                    ).filter((p) => {
                      const productId =
                        'listing_product_id' in p ? p.listing_product_id : p.id;
                      return productId !== id;
                    });
                    return {
                      ...delivery,
                      products: filteredProducts as typeof delivery.products,
                    };
                  }
                  return delivery;
                });
                setCurrentDeliveryData(updatedDelivery);
              }
            }}
            onEditDelivery={(id) => {
              setCurrentStep(2);
              setIsDataChecked(false);
            }}
            onDeleteDelivery={(id) => {
              const deliveryType = form.getFieldValue('delivery_type');

              if (deliveryType === 'detailed_delivery') {
                if (currentDeliveryData && currentDeliveryData.length > 1) {
                  const updated = currentDeliveryData.filter(
                    (d, i) => (d.id || i) !== id
                  );
                  setCurrentDeliveryData(updated);
                } else {
                  form.setFieldValue('delivery_type', 'delivery_conditions');
                  setCurrentDeliveryData(null);
                }
              } else {
                form.setFieldsValue({
                  delivery_type: 'delivery_conditions',
                  contract_period_type: undefined,
                  contract_start_date: undefined,
                  contract_end_date: undefined,
                  delivery_period_value: undefined,
                  delivery_period_unit: undefined,
                  delivery_frequency_value: undefined,
                  delivery_frequency_unit: undefined,
                  free_form_description: undefined,
                });
                setCurrentDeliveryData(null);
              }
            }}
            onViewContract={(file) => {
              handleFileDownload(file as CustomUploadFile);
            }}
          />
        ),
      });
    }

    return baseSteps;
  }, [
    listingId,
    isLoadingListing,
    currentProducts,
    farmQualityMarksArray,
    farmQualityMarkIds,
    storageUnits,
    currentDeliveryData,
    tabValidationErrors,
    isDataChecked,
    cachedListing?.status,
    currentContractDocuments,
    form,
    allQualityMarks,
    draftId,
    productQualityMarks,
  ]);

  const hasValidationErrors = useCallback(() => {
    const fieldsError = form.getFieldsError();
    const hasErrors = fieldsError.some(({ errors }) => errors.length > 0);

    if (hasErrors) {
      const newTabErrors: { [tabKey: string]: number } = {};

      fieldsError.forEach(({ name, errors }) => {
        if (errors.length > 0) {
          const fieldName = Array.isArray(name)
            ? String(name[0])
            : String(name);

          if (stepFieldMap.add_listing_info.includes(fieldName)) {
            newTabErrors.add_listing_info =
              (newTabErrors.add_listing_info || 0) + 1;
          } else if (stepFieldMap.add_listing_products.includes(fieldName)) {
            newTabErrors.add_listing_products =
              (newTabErrors.add_listing_products || 0) + 1;
          } else if (stepFieldMap.add_delivery_conditions.includes(fieldName)) {
            newTabErrors.add_delivery_conditions =
              (newTabErrors.add_delivery_conditions || 0) + 1;
          } else if (
            stepFieldMap.add_listing_contract_terms.includes(fieldName)
          ) {
            newTabErrors.add_listing_contract_terms =
              (newTabErrors.add_listing_contract_terms || 0) + 1;
          }
        }
      });

      setTabValidationErrors(newTabErrors);
    } else {
      setTabValidationErrors({});
    }

    return hasErrors;
  }, [form, setTabValidationErrors]);

  const processDeliveryData = useCallback(
    (
      deliveryData: DeliveryConditionsData[],
      mainProducts: ListingProduct[] = []
    ) => {
      return deliveryData.map((delivery) => {
        const processedDelivery = { ...delivery };
        if (!listingId && processedDelivery.id) {
          delete processedDelivery.id;
        }

        if (
          delivery.delivery_type === 'detailed_delivery' &&
          delivery.products
        ) {
          const productsArray = delivery.products || [];
          const processedProducts: DeliveryProductReference[] = [];

          for (const product of productsArray) {
            const deliveryRef = product as DeliveryProductReference;

            if (
              !('delivery_quantity' in deliveryRef) ||
              !deliveryRef.delivery_quantity
            ) {
              continue;
            }

            let listingProductId =
              deliveryRef.listing_product_id || deliveryRef.id;

            if (
              !listingProductId &&
              deliveryRef.category_id &&
              mainProducts.length > 0
            ) {
              const matchedProduct = mainProducts.find(
                (p) => p.category_id === deliveryRef.category_id
              );
              listingProductId = matchedProduct?.id;
            }

            if (listingProductId) {
              processedProducts.push({
                listing_product_id: String(listingProductId),
                delivery_quantity: deliveryRef.delivery_quantity,
              });
            } else if (deliveryRef.category_id) {
              processedProducts.push({
                category_id: deliveryRef.category_id,
                delivery_quantity: deliveryRef.delivery_quantity,
              });
            }
          }

          processedDelivery.products = processedProducts;
        }

        if (delivery.delivery_type === 'detailed_delivery') {
          processedDelivery.delivery_frequency_value =
            delivery.delivery_frequency_value;
          processedDelivery.delivery_frequency_unit =
            delivery.delivery_frequency_unit;
        }

        return processedDelivery;
      });
    },
    [listingId]
  );

  const performDraftSave = useCallback(
    (context?: string) => {
      const isUnpublishedStatus =
        !cachedListing || cachedListing.status === 'unpublished';
      const isPublishing = context === 'publishing';

      if (!isPublishing && !isUnpublishedStatus && hasValidationErrors()) {
        message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
        return;
      }

      if (isPublishing) {
        try {
          form.validateFields();
        } catch (validationError) {
          message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
          return;
        }
      }

      const formData = form.getFieldsValue(true);

      if (
        !ignoreDeadlineValidation &&
        cachedListing &&
        cachedListing.status === 'unpublished' &&
        !cachedListing.is_pending_publishing
      ) {
        const publicationStartDate = formData.publication_start_date;
        if (!publicationStartDate || publicationStartDate.isBefore(dayjs())) {
          form.setFieldValue('publication_start_date', dayjs());
          formData.publication_start_date = dayjs();
        }
      }

      const {
        products,
        products_items,
        delivery_type,
        contract_period_type,
        contract_term_start_date,
        contract_term_end_date,
        payment_conditions,
        additional_contract_terms,
        delivery_period_value,
        delivery_period_unit,
        delivery_frequency_value,
        delivery_frequency_unit,
        free_form_description,
        ...otherData
      } = formData;

      const productsToProcess =
        currentProducts.length > 0 ? currentProducts : products_items || [];
      const lastSavedProducts = (lastSavedData as any)?.products_items as
        | ListingProduct[]
        | undefined;

      const savedProducts = lastSavedProducts || [];

      const processedProducts = productsToProcess.map(
        (product: ListingProduct) => {
          let productWithId = { ...product };

          if (!product.id && product.category_id) {
            const savedProduct = savedProducts.find(
              (sp: ListingProduct) => sp.category_id === product.category_id
            );
            if (savedProduct?.id) {
              productWithId.id = savedProduct.id;
            }
          }

          const processedProduct = {
            ...productWithId,
            quality_mark_ids: product.quality_mark_ids || [],
          };

          return processedProduct;
        }
      );

      const deliveryData = currentDeliveryData || [];
      const processedDeliveryData = processDeliveryData(
        deliveryData,
        currentProducts
      );

      const contractTerms = serializeContractTerms(
        contract_term_start_date,
        contract_term_end_date,
        payment_conditions,
        additional_contract_terms
      );

      const submitData = {
        ...otherData,
        farm_id: farmId ?? user?.farmId,
        status: listingId
          ? cachedListing?.status || 'unpublished'
          : 'unpublished',
        products: processedProducts,
        delivery_conditions: processedDeliveryData,
        contract_terms: contractTerms,
      };

      saveDraftData(
        submitData,
        {},
        {
          context,
        }
      );
    },
    [
      form,
      farmId,
      user?.farmId,
      saveDraftData,
      currentDeliveryData,
      currentProducts,
      processDeliveryData,
      listingId,
      hasValidationErrors,
      intl,
      cachedListing,
      lastSavedData,
      ignoreDeadlineValidation,
    ]
  );

  const normalizeFormDataForComparison = useCallback(
    (data: Record<string, unknown>): Record<string, unknown> => {
      if (!data || typeof data !== 'object') {
        return data;
      }

      const normalized: Record<string, unknown> = { ...data };

      const isDayjs = (value: unknown): value is dayjs.Dayjs => {
        return (
          value !== null &&
          typeof value === 'object' &&
          'toISOString' in value &&
          typeof (value as { toISOString?: () => string }).toISOString ===
            'function'
        );
      };

      Object.keys(normalized).forEach((key) => {
        const value = normalized[key];

        if (typeof value === 'string' && value.trim() === '') {
          normalized[key] = null;
        }

        if (
          key === 'publication_start_date' ||
          key === 'publication_end_date' ||
          key === 'contract_term_start_date' ||
          key === 'contract_term_end_date'
        ) {
          if (isDayjs(value)) {
            normalized[key] = value.toISOString();
          } else if (value === null || value === undefined) {
            normalized[key] = null;
          }
        } else if (key === 'delivery_conditions' && Array.isArray(value)) {
          normalized[key] = value.map((delivery) => {
            if (!delivery || typeof delivery !== 'object') {
              return delivery;
            }
            const normalizedDelivery = { ...delivery };
            if (isDayjs(normalizedDelivery.delivery_start_date)) {
              normalizedDelivery.delivery_start_date = (
                normalizedDelivery.delivery_start_date as dayjs.Dayjs
              ).toISOString();
            }
            if (isDayjs(normalizedDelivery.delivery_end_date)) {
              normalizedDelivery.delivery_end_date = (
                normalizedDelivery.delivery_end_date as dayjs.Dayjs
              ).toISOString();
            }
            return normalizedDelivery;
          });
        }
      });

      return normalized;
    },
    []
  );

  const {
    hasUnsavedChanges,
    checkForUnsavedChanges,
    resetAutoSave,
    setDraftSaveStatus,
  } = useAutoSave({
    form,
    isOpen: open || false,
    isSaving: isDraftSaving,
    isUploading: isUploadingFile,
    onSave: performDraftSave,
    getLastSavedData: () => lastSavedData,
    setLastSavedData: (data) => setLastSavedData(data as Partial<Listing>),
    getAdditionalData: () => ({
      delivery_conditions: currentDeliveryData,
    }),
    isEnabled: () => {
      if (viewOnly) return false;
      const enabled =
        (listingId &&
          cachedListing?.status &&
          (cachedListing.status === 'unpublished' ||
            cachedListing.status === 'published' ||
            cachedListing.status === 'has_offers')) ||
        !listingId;
      return enabled;
    },
    onValidationError: hasValidationErrors,
    normalizeData: normalizeFormDataForComparison,
    compareFields: compareFieldsArray,
  });

  setDraftSaveStatusRef.current = setDraftSaveStatus;

  const resetEverything = useCallback(() => {
    setCachedListing(null);
    form.resetFields();
    form.setFieldValue('products_items', []);
    setCurrentProducts([]);
    setCurrentDeliveryData(null);
    setCurrentStep(0);
    setDraftId(null);
    setIsFirstSave(true);
    setLastSavedData(null);
    resetAutoSave();
    setIsProductsFormOpen(false);
    setTabValidationErrors({});
    setIsPublishing(false);
    setIsDataChecked(false);
    setIsCheckingData(false);
    setCurrentContractDocuments([]);
  }, [form, resetAutoSave]);

  const saveDraftPromise = useCallback(
    async (context?: string): Promise<string | number | null> => {
      if (isDraftSaving || isUploadingFile) {
        return draftId || listingId || null;
      }

      // Skip validation error check for unpublished listings when NOT publishing
      const isUnpublishedStatus =
        !cachedListing || cachedListing.status === 'unpublished';
      const isPublishing = context === 'publishing';

      if (!isUnpublishedStatus && hasValidationErrors()) {
        message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
        return null;
      }

      // Run full validation when publishing OR when listing is not unpublished
      if (
        isPublishing ||
        (cachedListing && cachedListing.status !== 'unpublished')
      ) {
        try {
          await form.validateFields();
        } catch (validationError) {
          message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
          return null;
        }
      }

      return new Promise<string | number | null>((resolve, reject) => {
        const originalResolve = window.saveDraftResolve;
        const originalReject = window.saveDraftReject;

        let isResolved = false;
        const isExplicitSave = context === 'explicit_save';

        window.saveDraftResolve = (
          listingIdFromSave?: string | number | null
        ) => {
          if (isResolved) return;
          isResolved = true;

          const resolvedListingId =
            listingIdFromSave !== undefined && listingIdFromSave !== null
              ? listingIdFromSave
              : draftId || listingId || null;
          if (originalResolve) {
            originalResolve(listingIdFromSave);
          }
          resolve(resolvedListingId);
          window.saveDraftResolve = originalResolve || undefined;
          window.saveDraftReject = originalReject || undefined;

          if (isExplicitSave) {
            resetEverything();
            if (closeDrawer) {
              closeDrawer(false);
            } else if (onClose) {
              onClose({} as React.MouseEvent);
            }
          }
        };

        window.saveDraftReject = () => {
          if (isResolved) return;
          isResolved = true;

          if (originalReject) {
            originalReject();
          }
          reject();
          window.saveDraftResolve = originalResolve || undefined;
          window.saveDraftReject = originalReject || undefined;
        };

        setDraftSaveStatus({ status: 'saving' });
        message.loading(intl.formatMessage({ id: 'general.saving_draft' }), 0);

        try {
          performDraftSave(context);
        } catch (error) {
          if (!isResolved) {
            isResolved = true;
            reject(
              error instanceof Error
                ? error
                : new Error(intl.formatMessage({ id: 'validation.error' }))
            );
            window.saveDraftResolve = originalResolve || undefined;
            window.saveDraftReject = originalReject || undefined;
          }
        }
      });
    },
    [
      isDraftSaving,
      isUploadingFile,
      draftId,
      listingId,
      hasValidationErrors,
      intl,
      setDraftSaveStatus,
      performDraftSave,
      closeDrawer,
      onClose,
      resetEverything,
    ]
  );

  useEffect(() => {
    if (open) {
      setDraftId(null);
      setIsFirstSave(true);
      setLastSavedData(null);
      resetAutoSave();

      if (!listingId) {
        setCurrentContractDocuments([]);
      }
    }
  }, [open, listingId, form, resetAutoSave]);

  const handleFormValuesChange = useCallback(
    (
      changedValues: Record<string, unknown>,
      allValues: Record<string, unknown>
    ) => {
      const hasTrackedFieldChanges = Object.keys(changedValues).some((key) =>
        [
          'title',
          'identifier',
          'transaction_type',
          'transaction_context',
          'description',
          'is_private',
          'single_producer_only',
          'private_link_full',
          'publication_start_date',
          'publication_end_date',
          'auto_extend_publication',
          'extend_days',
          'delivery_radius_km',
          'address',
          'quality_marks',
          'contract_term_start_date',
          'contract_term_end_date',
          'payment_conditions',
          'additional_contract_terms',
          'contract_document',
        ].includes(key)
      );

      if (!hasTrackedFieldChanges) {
        const currentFormValues = form.getFieldsValue(true);
        const additionalData = {
          delivery_conditions: currentDeliveryData,
        };
        const fullCurrentData: Record<string, unknown> = {
          ...currentFormValues,
          ...additionalData,
        };

        compareFieldsArray.forEach((field) => {
          if (!(field in fullCurrentData)) {
            fullCurrentData[field] = undefined;
          }
        });

        setLastSavedData(fullCurrentData as Partial<Listing>);
      }

      checkForUnsavedChanges();

      const currentStepKey = steps[currentStep].key;
      if (tabValidationErrors[currentStepKey]) {
        setTabValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[currentStepKey];
          return newErrors;
        });
      }
    },
    [
      checkForUnsavedChanges,
      tabValidationErrors,
      currentStep,
      steps,
      currentDeliveryData,
    ]
  );

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const handleDataCheck = async () => {
    setIsCheckingData(true);

    try {
      const isValid = await validateAllTabs();

      if (!isValid) {
        message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
        setIsCheckingData(false);
        return;
      }

      await saveDraftPromise('data_check');

      setIsDataChecked(true);
      setCurrentStep(4);
    } catch (error: unknown) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error(intl.formatMessage({ id: 'validation.error' }));
      }
    } finally {
      setIsCheckingData(false);
    }
  };

  const handleClose = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (!hasUnsavedChanges) {
      resetEverything();
      if (onClose) {
        onClose(e);
      }
      return;
    }

    Modal.confirm({
      title: intl.formatMessage({ id: 'general.confirm_close' }),
      content: intl.formatMessage({
        id: 'general.do_you_really_want_to_cancel_creation',
      }),
      okText: intl.formatMessage({ id: 'general.cancel' }),
      cancelText: intl.formatMessage({ id: 'general.go_back' }),
      onOk: () => {
        resetEverything();
        if (onClose) {
          onClose(e);
        }
      },
    });
  };

  function isValidationError(
    error: unknown
  ): error is { errorFields: { name: string[] }[] } {
    return (
      typeof error === 'object' && error !== null && 'errorFields' in error
    );
  }

  const validateAllTabs = async () => {
    const errors: { [tabKey: string]: number } = {};

    try {
      const baseInfoFields = stepFieldMap.add_listing_info;
      try {
        await form.validateFields(baseInfoFields);

        const isUnpublishedStatus =
          !cachedListing ||
          (cachedListing.status === 'unpublished' &&
            !cachedListing.is_pending_publishing);

        if (isUnpublishedStatus && !ignoreDeadlineValidation) {
          const publicationEndDate = form.getFieldValue('publication_end_date');
          const currentPublicationStartDate = form.getFieldValue(
            'publication_start_date'
          );

          if (currentPublicationStartDate && publicationEndDate) {
            if (
              publicationEndDate.isBefore(currentPublicationStartDate) ||
              publicationEndDate.isSame(currentPublicationStartDate)
            ) {
              form.setFields([
                {
                  name: 'publication_end_date',
                  errors: [
                    intl.formatMessage({
                      id: 'validation.end_date_after_start',
                    }),
                  ],
                },
              ]);
              errors.add_listing_info = (errors.add_listing_info || 0) + 1;
            }
          }
        }
      } catch (error) {
        if (isValidationError(error)) {
          errors.add_listing_info = error.errorFields.length;
        }
      }

      const contractFields = stepFieldMap.add_listing_contract_terms;
      try {
        await form.validateFields(contractFields);
      } catch (error) {
        if (isValidationError(error)) {
          errors.add_listing_contract_terms = error.errorFields.length;
        }
      }

      if (!currentProducts || currentProducts.length === 0) {
        errors.add_listing_products = 1;
      }

      setTabValidationErrors(errors);
      return Object.keys(errors).length === 0;
    } catch (error) {
      return false;
    }
  };

  const changeStep = (desiredStep: string) => {
    const index = steps.findIndex((step) => step.key === desiredStep);

    if (index === -1) {
      return;
    }

    if (desiredStep !== 'add_listing_summary' && isDataChecked) {
      setIsDataChecked(false);
    }

    setCurrentStep(index);
  };

  const prevStep = () => {
    if (isDataChecked) {
      setIsDataChecked(false);
    }
    setCurrentStep(currentStep - 1);
  };

  const handlePublish = async () => {
    setIsPublishing(true);

    try {
      const isValid = await validateAllTabs();

      if (!isValid) {
        message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
        setIsPublishing(false);
        return;
      }

      if (
        !ignoreDeadlineValidation &&
        (!cachedListing ||
          (cachedListing.status === 'unpublished' &&
            !cachedListing.is_pending_publishing))
      ) {
        const publicationStartDate = form.getFieldValue(
          'publication_start_date'
        );

        if (!publicationStartDate || publicationStartDate.isBefore(dayjs())) {
          form.setFieldValue('publication_start_date', dayjs());
        }
      }

      const savedListingId = await saveDraftPromise('publishing');

      const listingIdToPublish = savedListingId || draftId || listingId;

      if (!listingIdToPublish) {
        message.error(intl.formatMessage({ id: 'validation.error' }));
        setIsPublishing(false);
        return;
      }

      publishListing({}, { listingId: listingIdToPublish });
    } catch (error: unknown) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error(intl.formatMessage({ id: 'validation.error' }));
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const titleContent = (
    <StyledDrawerTitle>
      <div className="title">
        <Label
          label={intl.formatMessage({
            id: listingId
              ? 'listings.edit_listing_title'
              : 'listings.create_listing_title',
          })}
          title={true}
        />
        <div className="title-cancel">
          {cachedListing?.status === 'cancelled' && (
            <>
              <Tag className={`status ${cachedListing.status}`}>
                {intl.formatMessage({ id: `listings.${cachedListing.status}` })}
              </Tag>
              <TooltipWithIcon
                title={
                  cachedListing.cancel_reason ? (
                    <>
                      {intl.formatMessage({
                        id:
                          'enum.' +
                          cachedListing.cancel_reason
                            .toUpperCase()
                            .replace(/-/g, '_'),
                        defaultMessage: cachedListing.cancel_reason,
                      })}
                      {cachedListing.cancel_reason === 'cancelled_other' &&
                        cachedListing.cancel_reason_description && (
                          <>
                            <br />
                            {cachedListing.cancel_reason_description}
                          </>
                        )}
                    </>
                  ) : (
                    intl.formatMessage({ id: 'listings.cancel_reason' })
                  )
                }
                placement="bottom"
              />
            </>
          )}
        </div>
      </div>
    </StyledDrawerTitle>
  );

  const Footer = useCallback(
    () => (
      <StyledFooter className={isProductsFormOpen ? 'disabled' : ''}>
        <div className={'btns'}>
          <Button
            className={'cancel-button'}
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={handleClose}
            disabled={isProductsFormOpen}
          />

          {!viewOnly &&
            (!listingId ||
              (cachedListing?.status &&
                isEditableStatus(cachedListing.status))) && (
              <Button
                label={intl.formatMessage({
                  id:
                    cachedListing?.status === 'unpublished' || !listingId
                      ? 'general.save_draft'
                      : 'listings.save_listing',
                })}
                type={'primary'}
                onClick={() =>
                  saveDraftPromise(
                    cachedListing?.status === 'unpublished' || !listingId
                      ? 'explicit_save'
                      : undefined
                  )
                }
                loading={isDraftSaving}
                disabled={isProductsFormOpen}
              />
            )}
          {currentStep > 0 && (
            <Button
              label={intl.formatMessage({ id: 'general.previous' })}
              onClick={prevStep}
              disabled={isProductsFormOpen}
            />
          )}
          {currentStep < steps.length - 1 && currentStep !== 3 && (
            <Button
              label={intl.formatMessage({ id: 'general.next_step' })}
              type={'primary'}
              onClick={nextStep}
              disabled={isProductsFormOpen}
            />
          )}
          {!viewOnly && currentStep === 3 && !isDataChecked && (
            <Button
              label={intl.formatMessage({ id: 'listings.form.data_check' })}
              type={'primary'}
              onClick={handleDataCheck}
              loading={isCheckingData}
              disabled={isProductsFormOpen}
            />
          )}
          {!viewOnly &&
            currentStep === steps.length - 1 &&
            isDataChecked &&
            (!listingId ||
              (cachedListing?.status &&
                isPublishableStatus(cachedListing.status) &&
                !cachedListing?.is_pending_publishing)) && (
              <Button
                label={intl.formatMessage({ id: 'general.publish' })}
                type={'primary'}
                onClick={handlePublish}
                loading={isPublishing || isPublishingApi}
                disabled={isProductsFormOpen}
              />
            )}
        </div>
      </StyledFooter>
    ),
    [
      intl,
      viewOnly,
      listingId,
      cachedListing?.status,
      isProductsFormOpen,
      isDraftSaving,
      currentStep,
      steps.length,
      isDataChecked,
      isCheckingData,
      isPublishing,
      isPublishingApi,
      saveDraftPromise,
      handleDataCheck,
      handlePublish,
    ]
  );

  return (
    <StyledListingDrawer
      key={listingId || 'new'}
      open={open}
      onClose={handleClose}
      footer={<Footer />}
      title={titleContent}
      width={1000}
      zIndex={1100}
      closeIcon={<Icon name={'global-close-icon'} type={'small'} />}
      destroyOnClose
    >
      <Spinner spinning={isPublishingApi} dontRender>
        <Form
          form={form}
          layout={'vertical'}
          onValuesChange={handleFormValuesChange}
        >
          <Tabs
            onChange={(desiredStep) => {
              changeStep(desiredStep);
            }}
            activeKey={steps[currentStep].key}
            items={steps.map((step) => ({
              key: step.key,
              label: step.label,
              children: step.content,
              forceRender: true,
            }))}
            tabValidationErrors={tabValidationErrors}
          />
        </Form>
      </Spinner>
    </StyledListingDrawer>
  );
};
